import {
  EnvironmentProviders,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';

/** Opt a `<details>` into outside-click dismiss. */
export const RIND_DISMISS_OUTSIDE_ATTR = 'data-rind-dismiss-outside';

/** Default selector for auto-tracked popover `<details>`. */
export const RIND_DISMISS_OUTSIDE_DETAILS_SELECTOR = `details[${RIND_DISMISS_OUTSIDE_ATTR}]`;

/**
 * Anything that can be dismissed when the user interacts outside it
 * (custom popovers, menus, overlays — not only `<details>`).
 */
export interface OutsideDismissable {
  contains(node: Node): boolean;
  dismiss(): void;
}

export interface DismissOnOutsideOptions {
  /**
   * CSS selector for `<details>` to auto-track while open.
   * Defaults to {@link RIND_DISMISS_OUTSIDE_DETAILS_SELECTOR}.
   * Pass `null` to disable details auto-tracking (manual registry only).
   */
  detailsSelector?: string | null;
}

const active = new Set<OutsideDismissable>();
const detailsEntries = new WeakMap<HTMLDetailsElement, OutsideDismissable>();

let detailsSelector: string | null = RIND_DISMISS_OUTSIDE_DETAILS_SELECTOR;
let installed = false;
let pointerListening = false;

function syncPointerListener(): void {
  if (active.size > 0 && !pointerListening) {
    document.addEventListener('pointerdown', onPointerDown, true);
    pointerListening = true;
  } else if (active.size === 0 && pointerListening) {
    document.removeEventListener('pointerdown', onPointerDown, true);
    pointerListening = false;
  }
}

function onPointerDown(event: PointerEvent): void {
  const target = event.target;
  if (!(target instanceof Node) || active.size === 0) {
    return;
  }

  for (const entry of [...active]) {
    if (!entry.contains(target)) {
      entry.dismiss();
      active.delete(entry);
    }
  }
  syncPointerListener();
}

function trackDetails(details: HTMLDetailsElement): void {
  let entry = detailsEntries.get(details);
  if (!entry) {
    entry = {
      contains: (node) => details.isConnected && details.contains(node),
      dismiss: () => {
        details.open = false;
      },
    };
    detailsEntries.set(details, entry);
  }
  active.add(entry);
  syncPointerListener();
}

function untrackDetails(details: HTMLDetailsElement): void {
  const entry = detailsEntries.get(details);
  if (entry) {
    active.delete(entry);
  }
  syncPointerListener();
}

function onToggle(event: Event): void {
  const target = event.target;
  if (!(target instanceof HTMLDetailsElement)) {
    return;
  }
  if (detailsSelector === null || !target.matches(detailsSelector)) {
    return;
  }
  if (target.open) {
    trackDetails(target);
  } else {
    untrackDetails(target);
  }
}

/**
 * Register a non-`<details>` popover/menu for outside dismiss.
 * Returns an unregister function (also call when the host is destroyed).
 */
export function registerOutsideDismissable(entry: OutsideDismissable): () => void {
  active.add(entry);
  syncPointerListener();
  return () => {
    active.delete(entry);
    syncPointerListener();
  };
}

/** Dismiss every currently tracked popover. */
export function dismissAllOutsideDismissables(): void {
  for (const entry of [...active]) {
    entry.dismiss();
  }
  active.clear();
  syncPointerListener();
}

/**
 * Install document listeners. Prefer {@link provideDismissOnOutside} in app config.
 * Idempotent — later calls update the details selector only before first install.
 */
export function installDismissOnOutside(options?: DismissOnOutsideOptions): void {
  if (options && 'detailsSelector' in options) {
    detailsSelector = options.detailsSelector ?? null;
  }

  if (installed || typeof document === 'undefined') {
    return;
  }
  installed = true;

  if (detailsSelector !== null) {
    document.addEventListener('toggle', onToggle, true);
  }
}

/**
 * Registers a performant outside-click dismiss mechanism:
 * one capture `toggle` listener (optional) and one `pointerdown` listener
 * only while at least one popover is open.
 */
export function provideDismissOnOutside(
  options?: DismissOnOutsideOptions,
): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(() => installDismissOnOutside(options)),
  ]);
}
